// S7 — floating PTZ control panel.
//
// Opened from the Live View tile's joystick icon. Owns its own preset
// fetch + state; the LiveView page only needs to know which camera id
// is active and how to close the panel.
//
// Movement model: hold a direction button → POST /move with that
// pan/tilt unit-vector. Release → POST /stop after a 150ms debounce so
// rapid taps don't queue a stop *after* the next move, which would
// produce a flicker. Speed slider scales both pan/tilt and zoom.
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowDownIcon,
  ArrowDownLeftIcon,
  ArrowDownRightIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpIcon,
  ArrowUpLeftIcon,
  ArrowUpRightIcon,
  PlusIcon,
  MinusIcon,
  SquareIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";

import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";


interface PTZControlsProps {
  cameraId: string;
  cameraName: string;
  onClose: () => void;
}

interface Preset {
  id: string;
  name: string;
  preset_token: string | null;
  preset_number: number | null;
  pan: number | null;
  tilt: number | null;
  zoom: number | null;
}

type Vec = { pan: number; tilt: number; zoom: number };

const DIRECTIONS: Array<{ key: string; vec: Vec; icon: React.ComponentType<{ className?: string }> }> = [
  { key: "NW", vec: { pan: -0.7, tilt: 0.7, zoom: 0 }, icon: ArrowUpLeftIcon },
  { key: "N", vec: { pan: 0, tilt: 1, zoom: 0 }, icon: ArrowUpIcon },
  { key: "NE", vec: { pan: 0.7, tilt: 0.7, zoom: 0 }, icon: ArrowUpRightIcon },
  { key: "W", vec: { pan: -1, tilt: 0, zoom: 0 }, icon: ArrowLeftIcon },
  // STOP slot is rendered separately so the parent grid still reads as 3x3.
  { key: "STOP", vec: { pan: 0, tilt: 0, zoom: 0 }, icon: SquareIcon },
  { key: "E", vec: { pan: 1, tilt: 0, zoom: 0 }, icon: ArrowRightIcon },
  { key: "SW", vec: { pan: -0.7, tilt: -0.7, zoom: 0 }, icon: ArrowDownLeftIcon },
  { key: "S", vec: { pan: 0, tilt: -1, zoom: 0 }, icon: ArrowDownIcon },
  { key: "SE", vec: { pan: 0.7, tilt: -0.7, zoom: 0 }, icon: ArrowDownRightIcon },
];


export function PTZControls({ cameraId, cameraName, onClose }: PTZControlsProps) {
  const [presets, setPresets] = useState<Preset[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [moving, setMoving] = useState<string | null>(null);
  const [speed, setSpeed] = useState(0.5);
  const [presetName, setPresetName] = useState("");
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const stopTimerRef = useRef<number | null>(null);
  // S18B-2 — repeat the move while a direction is held so motion is smooth
  // and continuous (some cameras stop after a single move command).
  const moveTimerRef = useRef<number | null>(null);

  const thumbKey = (presetId: string) => `sview360.ptzthumb.${cameraId}.${presetId}`;

  const loadThumbs = useCallback(
    (ps: Preset[]) => {
      const next: Record<string, string> = {};
      for (const p of ps) {
        try {
          const v = localStorage.getItem(thumbKey(p.id));
          if (v) next[p.id] = v;
        } catch {
          /* ignore */
        }
      }
      setThumbs(next);
    },
    [cameraId],
  );

  // Capture a real frame from the server-side snapshot endpoint (a JPEG blob —
  // NOT a client canvas, which would taint cross-origin) and store it as the
  // preset thumbnail. Best-effort: a failure just leaves a placeholder.
  const captureThumb = useCallback(
    async (presetId: string) => {
      try {
        const r = await api.get(`/api/v1/cameras/${cameraId}/snapshot`, {
          responseType: "blob",
        });
        const blob = r.data as Blob;
        const dataUrl: string = await new Promise((resolve, reject) => {
          const fr = new FileReader();
          fr.onload = () => resolve(String(fr.result));
          fr.onerror = reject;
          fr.readAsDataURL(blob);
        });
        localStorage.setItem(thumbKey(presetId), dataUrl);
        setThumbs((t) => ({ ...t, [presetId]: dataUrl }));
      } catch {
        /* snapshot unavailable (e.g. pure RTSP) — placeholder remains */
      }
    },
    [cameraId],
  );

  const fetchPresets = useCallback(async () => {
    setLoadingPresets(true);
    setError(null);
    try {
      const { data } = await api.get<Preset[]>(
        `/api/v1/ptz/${cameraId}/presets`,
      );
      setPresets(data);
      loadThumbs(data);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      setError(err.response?.data?.detail ?? "Failed to load presets");
    } finally {
      setLoadingPresets(false);
    }
  }, [cameraId, loadThumbs]);

  useEffect(() => {
    void fetchPresets();
    return () => {
      if (stopTimerRef.current !== null) {
        window.clearTimeout(stopTimerRef.current);
      }
      if (moveTimerRef.current !== null) {
        window.clearInterval(moveTimerRef.current);
      }
    };
  }, [fetchPresets]);

  const callMove = useCallback(
    async (vec: Vec) => {
      setError(null);
      try {
        await api.post(`/api/v1/ptz/${cameraId}/move`, {
          pan: vec.pan,
          tilt: vec.tilt,
          zoom: vec.zoom,
          speed,
        });
      } catch (e: unknown) {
        const err = e as { response?: { status?: number; data?: { detail?: string } } };
        if (err.response?.status === 502) {
          setError("Device unreachable");
        } else if (err.response?.status === 400) {
          setError(err.response?.data?.detail ?? "PTZ not available on this camera");
        } else {
          setError("PTZ move failed");
        }
      }
    },
    [cameraId, speed],
  );

  const callStop = useCallback(async () => {
    try {
      await api.post(`/api/v1/ptz/${cameraId}/stop`);
    } catch {
      // Don't surface stop errors — a failed stop is benign vs a
      // failed move, and the user already released the button.
    }
  }, [cameraId]);

  function handlePressStart(key: string, vec: Vec) {
    setMoving(key);
    if (stopTimerRef.current !== null) {
      window.clearTimeout(stopTimerRef.current);
      stopTimerRef.current = null;
    }
    if (moveTimerRef.current !== null) {
      window.clearInterval(moveTimerRef.current);
      moveTimerRef.current = null;
    }
    if (key === "STOP") {
      void callStop();
      return;
    }
    void callMove(vec);
    // Continuous motion: re-issue the move while the button stays held.
    moveTimerRef.current = window.setInterval(() => {
      void callMove(vec);
    }, 350);
  }

  function handlePressEnd() {
    setMoving(null);
    if (moveTimerRef.current !== null) {
      window.clearInterval(moveTimerRef.current);
      moveTimerRef.current = null;
    }
    // Debounce the stop so a rapid down/up doesn't issue stop before
    // the move has reached the device.
    if (stopTimerRef.current !== null) {
      window.clearTimeout(stopTimerRef.current);
    }
    stopTimerRef.current = window.setTimeout(() => {
      void callStop();
      stopTimerRef.current = null;
    }, 150);
  }

  async function handleGoto(presetId: string) {
    setError(null);
    setMoving(presetId);
    try {
      await api.post(`/api/v1/ptz/${cameraId}/presets/${presetId}/goto`);
      // Visual "Moving..." indicator for ~1s.
      window.setTimeout(() => setMoving(null), 1000);
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      setError(
        err.response?.status === 502
          ? "Device unreachable"
          : err.response?.data?.detail ?? "Goto preset failed",
      );
      setMoving(null);
    }
  }

  async function handleSavePosition() {
    if (!presetName.trim()) {
      setError("Enter a preset name");
      return;
    }
    setError(null);
    try {
      const { data } = await api.post<Preset>(`/api/v1/ptz/${cameraId}/presets`, {
        name: presetName.trim(),
      });
      setPresetName("");
      // Capture a thumbnail of the current view for this preset (best-effort).
      if (data?.id) void captureThumb(data.id);
      await fetchPresets();
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { detail?: string } } };
      setError(err.response?.data?.detail ?? "Failed to save preset");
    }
  }

  async function handleDelete(presetId: string) {
    if (!window.confirm("Delete this preset?")) return;
    setError(null);
    try {
      await api.delete(`/api/v1/ptz/${cameraId}/presets/${presetId}`);
      try {
        localStorage.removeItem(thumbKey(presetId));
      } catch {
        /* ignore */
      }
      await fetchPresets();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err.response?.data?.detail ?? "Failed to delete preset");
    }
  }

  return (
    <div
      role="dialog"
      aria-label={`PTZ controls for ${cameraName}`}
      className="fixed right-4 top-24 z-50 w-72 rounded-md border border-[#2d3f4f] bg-primary p-3 text-white shadow-xl"
    >
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="text-sm font-medium">
          PTZ — <span className="text-[#ff6d00]">{cameraName}</span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close PTZ controls"
          className="rounded p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      {/* Direction pad */}
      <div className="mb-3 grid grid-cols-3 gap-1.5">
        {DIRECTIONS.map(({ key, vec, icon: Icon }) => (
          <button
            key={key}
            type="button"
            onPointerDown={() => handlePressStart(key, vec)}
            onPointerUp={handlePressEnd}
            onPointerLeave={moving === key ? handlePressEnd : undefined}
            onPointerCancel={handlePressEnd}
            className={cn(
              "flex aspect-square items-center justify-center rounded transition",
              key === "STOP"
                ? "bg-[#2d3f4f] hover:bg-[#384f63]"
                : "bg-[#21313c] hover:bg-[#2d3f4f]",
              moving === key && "ring-2 ring-[#ff6d00]",
            )}
            aria-label={`PTZ ${key}`}
          >
            <Icon className="h-4 w-4" />
          </button>
        ))}
      </div>

      {/* Zoom row */}
      <div className="mb-3 grid grid-cols-2 gap-1.5">
        <button
          type="button"
          onPointerDown={() => handlePressStart("ZOOM_IN", { pan: 0, tilt: 0, zoom: 1 })}
          onPointerUp={handlePressEnd}
          onPointerLeave={moving === "ZOOM_IN" ? handlePressEnd : undefined}
          className={cn(
            "flex items-center justify-center gap-1 rounded bg-[#21313c] py-2 text-xs transition hover:bg-[#2d3f4f]",
            moving === "ZOOM_IN" && "ring-2 ring-[#ff6d00]",
          )}
        >
          <PlusIcon className="h-3 w-3" /> Zoom
        </button>
        <button
          type="button"
          onPointerDown={() => handlePressStart("ZOOM_OUT", { pan: 0, tilt: 0, zoom: -1 })}
          onPointerUp={handlePressEnd}
          onPointerLeave={moving === "ZOOM_OUT" ? handlePressEnd : undefined}
          className={cn(
            "flex items-center justify-center gap-1 rounded bg-[#21313c] py-2 text-xs transition hover:bg-[#2d3f4f]",
            moving === "ZOOM_OUT" && "ring-2 ring-[#ff6d00]",
          )}
        >
          <MinusIcon className="h-3 w-3" /> Zoom
        </button>
      </div>

      {/* Speed slider */}
      <div className="mb-3">
        <div className="mb-1 flex items-center justify-between text-[11px] text-white/70">
          <span>Speed</span>
          <span className="text-white">{speed.toFixed(1)}</span>
        </div>
        <input
          type="range"
          min={0.1}
          max={1.0}
          step={0.1}
          value={speed}
          onChange={(e) => setSpeed(parseFloat(e.target.value))}
          className="w-full accent-[#ff6d00]"
          aria-label="PTZ speed"
        />
      </div>

      {/* Presets */}
      <div className="mb-2 text-[11px] font-medium uppercase text-white/70">
        Presets
      </div>
      {loadingPresets ? (
        <div className="text-[11px] text-white/50">Loading presets…</div>
      ) : presets.length === 0 ? (
        <div className="text-[11px] text-white/50">No presets saved.</div>
      ) : (
        // S18B-2 — preset thumbnail grid: click a card to recall.
        <div className="mb-2 grid grid-cols-2 gap-1.5">
          {presets.map((p) => (
            <div
              key={p.id}
              className="group relative overflow-hidden rounded bg-[#21313c]"
            >
              <button
                type="button"
                onClick={() => void handleGoto(p.id)}
                aria-label={`Recall preset ${p.name}`}
                className="block w-full"
              >
                <div className="relative aspect-video bg-[#0f1923]">
                  {thumbs[p.id] ? (
                    <img
                      src={thumbs[p.id]}
                      alt={p.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-[10px] text-white/30">
                      no thumbnail
                    </div>
                  )}
                  {moving === p.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-[10px] text-[#ff6d00]">
                      Moving…
                    </div>
                  )}
                </div>
                <div className="truncate px-1 py-0.5 text-left text-[10px]">{p.name}</div>
              </button>
              <button
                type="button"
                onClick={() => void handleDelete(p.id)}
                aria-label={`Delete preset ${p.name}`}
                className="absolute right-0.5 top-0.5 rounded bg-black/50 p-0.5 text-white/70 opacity-0 transition hover:text-red-400 group-hover:opacity-100"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Save current position */}
      <div className="mb-2 flex items-center gap-1">
        <input
          type="text"
          placeholder="Preset name"
          value={presetName}
          onChange={(e) => setPresetName(e.target.value)}
          className="flex-1 rounded border border-[#2d3f4f] bg-[#0f1923] px-2 py-1 text-[11px] text-white placeholder:text-white/30"
        />
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2 text-[11px]"
          onClick={() => void handleSavePosition()}
        >
          Save
        </Button>
      </div>

      {error && (
        <div className="mt-2 rounded bg-red-950/40 px-2 py-1 text-[11px] text-red-200">
          {error}
        </div>
      )}
    </div>
  );
}
